import { debounce } from '../utils/debounce.js';

const DB_NAME = 'kips-design';
const STORE = 'projects';
const FALLBACK_KEY = 'kips-design:last-project';

export class AutoSaveManager extends EventTarget {
  constructor(wait = 700) {
    super();
    this.schedule = debounce((project) => this.saveNow(project), wait);
  }

  queue(project) {
    this.#status('saving');
    this.schedule(structuredClone(project));
  }

  async saveNow(project) {
    try {
      const db = await this.#open();
      await new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE, 'readwrite');
        transaction.objectStore(STORE).put({ key: 'last', project, savedAt: Date.now() });
        transaction.oncomplete = resolve;
        transaction.onerror = () => reject(transaction.error);
      });
      this.#status(navigator.onLine ? 'saved' : 'offline');
    } catch (error) {
      try {
        localStorage.setItem(FALLBACK_KEY, JSON.stringify({ project, savedAt: Date.now() }));
        this.#status('offline');
      } catch {
        this.#status('error', error);
      }
    }
  }

  async loadLast() {
    try {
      const db = await this.#open();
      const record = await new Promise((resolve, reject) => {
        const request = db.transaction(STORE, 'readonly').objectStore(STORE).get('last');
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      return record?.project ?? null;
    } catch {
      try { return JSON.parse(localStorage.getItem(FALLBACK_KEY))?.project ?? null; } catch { return null; }
    }
  }

  #open() {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in globalThis)) return reject(new Error('IndexedDB unavailable'));
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE, { keyPath: 'key' });
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  #status(status, error = null) {
    const event = typeof CustomEvent === 'function' ? new CustomEvent('status', { detail: { status, error } }) : new Event('status');
    if (!('detail' in event)) Object.defineProperty(event, 'detail', { value: { status, error } });
    this.dispatchEvent(event);
  }
}
